import React, {
  memo,
  useCallback,
  useMemo,
  FC,
  useState,
  useEffect,
} from 'react'

import { FocusNode, useSetFocus } from '@please/lrud'
import { useNavigate } from 'react-router-dom'
import { Element } from 'react-scroll'

import { AutoHide } from '@/components/AutoHide/AutoHide'
import { ChannelImage } from '@/components/ChannelImage/ChannelImage'
import { ChannelLine } from '@/components/Lines/ChannelLine'
import { VideoHeading } from '@/components/VideoHeading/VideoHeading'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { scrollOptions } from '@/constants/scrollers'
import { BodyText, ScrollContainer } from '@/containers/styles'
import { useActiveBroadcast } from '@/hooks/useActiveBroadcast'
import { useDelayUnmount } from '@/hooks/useDelayUnmount'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { Keys } from '@/platform/platform'
import useStore from '@/store/useStore'
import { VideoControlsProps } from '@/types/componentsPropsTypes'
import { isWebos } from '@/utils/utils'

import { PlaybackControls } from './PlaybackControls'
import {
  ControlsContainer,
  StyledControlsContainer,
  FocusButtonNode,
  AnimationBox,
  Box,
  ShowChannelsButton,
  HideChannelsButton,
} from './styles'

const VideoControlsRaw: FC<VideoControlsProps> = ({
  domRect,
  videoElementRef,
  isFullscreen,
  videoData,
  showChannels,
  setIsVideoPlaying,
  isVideoPlaying = true,
  onExitButton,
  title,
  broadcastId = '',
  currentEvent,
  sliderData,
  focusIdPrefix = '',
  isVideoStarted = false,
  isHTMLWidget = false,
  channelFromProps,
}) => {
  const setFocus = useSetFocus()
  const navigate = useNavigate()
  const { t } = useCommonTranslation()

  const { videoControlsVisible, showVideoControls, hideVideoControls } =
    useStore((state) => state.player)
  const { activeChannel: channelFromBroadcastHook } =
    useActiveBroadcast(broadcastId)

  const activeChannel = channelFromBroadcastHook || channelFromProps

  const [displayLiveChannels, setDisplayLiveChannels] = useState<boolean>(false)
  const [isSliderMounted, setIsSliderMounted] = useState(false)

  const showDiv = useDelayUnmount(displayLiveChannels, 400)
  const isWebOs = isWebos()
  const showVideoPlayerControls = !isHTMLWidget && videoElementRef
  const defaultFocusId = isHTMLWidget
    ? focusIdPrefix + '24/7Channels'
    : focusIdPrefix + 'section_1'

  useEffect(() => {
    if (!videoControlsVisible) {
      setDisplayLiveChannels(false)
      setFocus(defaultFocusId)
    }
  }, [videoControlsVisible])

  useEffect(() => {
    if (displayLiveChannels) {
      !isSliderMounted && setIsSliderMounted(true)
      setTimeout(() => setFocus(focusIdPrefix + 'section_4'), 0)
    } else {
      setFocus(defaultFocusId)
    }
  }, [displayLiveChannels])

  const keyPressHandler = (e: KeyboardEvent) => {
    if (e.keyCode === Keys.MEDIA_STOP) {
      onExit()
    }
  }

  useEffect(() => {
    if (!isWebOs || !isHTMLWidget) {
      return
    }
    window.addEventListener('keydown', keyPressHandler)
    return () => {
      window.removeEventListener('keydown', keyPressHandler)
    }
  }, [isHTMLWidget])

  const memoizedLine = useMemo(
    () => ({
      lineContextId: 'liveChannels',
      liveToday: true,
      name: t('channels247'),
      static: null,
      allowHideIfEmpty: false,
    }),
    [],
  )

  const toggleShow = useCallback(
    (next: boolean) => {
      if (next) {
        showVideoControls()
      } else {
        hideVideoControls()
      }
    },
    [showVideoControls, hideVideoControls],
  )

  const options = useMemo(
    () => ({
      onHide: () => hideVideoControls(),
      onShow: () => showVideoControls(),
    }),
    [hideVideoControls, showVideoControls],
  )

  const onExit = (e?: LRUDEvent) => {
    hideVideoControls()
    if (onExitButton) {
      e?.stopPropagation()
      return onExitButton()
    }
    isFullscreen ? navigate(-1) : setFocus('VideoFocus')
  }

  return (
    <StyledControlsContainer
      $inFront={videoControlsVisible}
      $domRect={domRect}
      $isFullscreen={isFullscreen}
    >
      <AutoHide
        show={videoControlsVisible}
        onChange={toggleShow}
        options={options}
      >
        <ControlsContainer
          defaultFocusChild={1}
          focusId={focusIdPrefix + focusIds.VIDEO_CONTAINER}
          elementType="div"
          orientation={orientation.VERTICAL}
        >
          <AnimationBox
            $height="100%"
            $justifyContent="space-between"
            $isExiting={displayLiveChannels}
            $showDiv={!showDiv}
          >
            <Box $alignItems="center">
              <FocusButtonNode
                elementType="section"
                focusId={focusIdPrefix + 'section_2'}
                onSelected={onExit}
              >
                <img src={icons.navigation.backArrow} alt="" />
              </FocusButtonNode>
              <BodyText>{t('pressBack')}</BodyText>
            </Box>

            <Box $flexDirection="column">
              <div>
                <Box $justifyContent="space-between" $alignItems="center">
                  <VideoHeading
                    name={title}
                    currentEvent={currentEvent}
                    activeChannel={activeChannel}
                    isHTMLWidget={isHTMLWidget}
                  />
                  <div />
                  <ChannelImage sources={activeChannel?.image?.sources} />
                </Box>
                {showVideoPlayerControls && (
                  <PlaybackControls
                    videoElementRef={videoElementRef}
                    videoData={videoData}
                    setIsVideoPlaying={setIsVideoPlaying}
                    onExit={onExit}
                    currentEvent={currentEvent}
                    isVideoPlaying={isVideoPlaying}
                    isVideoStarted={isVideoStarted}
                    showChannels={showChannels}
                    setDisplayLiveChannels={() => {
                      showChannels &&
                        videoControlsVisible &&
                        setDisplayLiveChannels(true)
                    }}
                    displayLiveChannels={displayLiveChannels}
                    focusIdPrefix={focusIdPrefix}
                    displayVideoControls={videoControlsVisible}
                  />
                )}
                {showChannels && (
                  <Box $alignItems="center" $justifyContent="center">
                    <ShowChannelsButton
                      focusId={focusIdPrefix + '24/7Channels'}
                      onSelected={() => {
                        videoControlsVisible && setDisplayLiveChannels(true)
                      }}
                      onDown={() => {
                        videoControlsVisible && setDisplayLiveChannels(true)
                      }}
                    >
                      <Box $flexDirection="column" $alignItems="center">
                        <BodyText>{t('channels247')}</BodyText>
                        <img src={icons.controls.chevronDown} alt="" />
                      </Box>
                    </ShowChannelsButton>
                  </Box>
                )}
              </div>
            </Box>
          </AnimationBox>
          {showChannels && isSliderMounted && (
            <AnimationBox
              $alignSelf="flex-end"
              $isExiting={!displayLiveChannels}
              $showDiv={showDiv}
            >
              <HideChannelsButton
                focusId={focusIdPrefix + 'HideChannels'}
                onSelected={() => setDisplayLiveChannels(false)}
                onUp={() => setDisplayLiveChannels(false)}
              >
                <Box $flexDirection="column" $alignItems="center">
                  <img src={icons.controls.chevronUp} alt="" />

                  <BodyText>{t('back')}</BodyText>
                </Box>
              </HideChannelsButton>
              <FocusNode
                elementType="section"
                focusId={focusIdPrefix + 'section_4'}
                orientation={orientation.VERTICAL}
                onUp={() => setDisplayLiveChannels(false)}
              >
                <ScrollContainer
                  id={scrollOptions.PlayerChannelsScroll.containerId}
                  height={312}
                >
                  <Element
                    key={focusIds.LIVE_TODAY}
                    name="EventSlider_live_today_0"
                  >
                    <ChannelLine
                      nodeId={focusIds.LIVE_TODAY}
                      line={memoizedLine}
                      broadcastId={broadcastId}
                      sliderData={sliderData}
                    />
                  </Element>
                </ScrollContainer>
              </FocusNode>
            </AnimationBox>
          )}
        </ControlsContainer>
      </AutoHide>
    </StyledControlsContainer>
  )
}

export const VideoControls = memo(VideoControlsRaw)
